import React, { useContext, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import emailjs from '@emailjs/browser';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';
import { generateInvoicePDF, InvoiceTemplate } from '../admin/InvoiceGenerator';

const CheckoutPage = () => {
  const { cart, totalAmount, dispatch } = useContext(AppContext);
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const invoiceRef = useRef(null);

  const [form, setForm] = useState({ customerName: '', phone: '', city: '', buildingNo: '', street: '', area: '', pinCode: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // Bug Fix: Ensured state synchronization before calling PDF generator
  const handleDownloadInvoice = async () => {
    if (!orderData || !invoiceNumber) {
      alert("Invoice data is processing, please wait...");
      return;
    }
    try {
      await generateInvoicePDF(orderData, invoiceNumber);
    } catch (err) {
      console.error("PDF Error:", err);
      alert("Failed to download PDF.");
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (totalAmount < 2000) {
      setError('Order must be ₹2000 or more to proceed.');
      return;
    }

    setIsSubmitting(true);
    const generatedInvoiceNumber = `AK-${Date.now()}`;

    const orderPayload = {
      ...form,
      items: cart.map(item => ({
        productId: item.productId,
        name: item.name,
        quantity: item.quantity,
        offerPrice: item.offerPrice,
        subtotal: item.subtotal,
      })),
      totalAmount,
      invoiceNumber: generatedInvoiceNumber,
    };

    try {
      // API call to save order
      const response = await axios.post("https://ak-carckers-backend-api.onrender.com/api/orders", orderPayload);

      // Email notification (Non-blocking)
      try {
        const orderDate = new Date().toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        const itemsList = cart
          .map(item => `${item.name} x${item.quantity} - ₹${item.subtotal}`)
          .join('<br/>');

        const emailParams = {
          invoiceNumber: generatedInvoiceNumber,
          orderDate: orderDate,
          customerName: form.customerName,
          phone: form.phone,
          buildingNo: form.buildingNo,
          street: form.street,
          area: form.area,
          city: form.city,
          pinCode: form.pinCode,
          orderItems: itemsList,
          totalAmount: totalAmount,
        };

        console.log("Sending EmailJS with params:", emailParams);

        const emailRes = await emailjs.send(
          'service_o9gk8ft',
          'template_paa545d',
          emailParams,
          'CbUohC-9gEgXfvlWq'
        );

        console.log("EmailJS success:", emailRes.status, emailRes.text);
      } catch (e) {
        console.error("EmailJS FAILED:", e?.text || e?.message || JSON.stringify(e));
      }

      // Success State Update
      setOrderData(response.data);
      setInvoiceNumber(generatedInvoiceNumber);
      setSuccess(
        <>
          Your order has been received successfully! 🎉 <br />
          <small>Hold tight! Our team will reach out to you shortly for payment and to process your order.</small>
        </>
      );

      localStorage.removeItem('akCrackersCart');
      dispatch({ type: 'SET_CART', payload: [] });
    } catch (err) {
      setError('Server error, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // UI Flow logic maintained exactly as requested
  if (totalAmount < 2000 && !orderData) {
    return (
      <div className={`page-shell ${isDark ? 'bg-dark text-light' : ''}`} style={{ minHeight: '100vh' }}>
        <Navbar />
        <main className="container py-5 text-center">
          <div className="alert alert-warning">Minimum order is ₹2000.</div>
          <Link to="/products" className="btn btn-danger">Continue Shopping</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (orderData) {
    return (
      <div className={`page-shell ${isDark ? 'bg-dark text-light' : ''}`} style={{ minHeight: '100vh' }}>
        <Navbar />
        <main className="container py-4">
          <div className="alert alert-success">{success}</div>
          <div className="card p-4 border-0 shadow-sm" ref={invoiceRef}>
            <InvoiceTemplate order={orderData} invoiceNumber={invoiceNumber} />
            <div className="mt-4 d-flex gap-3">
              <button className="btn btn-danger" onClick={handleDownloadInvoice}>📥 Download PDF</button>
              <button className="btn btn-success" onClick={() => navigate('/products')}>Shop More</button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`page-shell ${isDark ? 'bg-dark text-light' : ''}`}>
      <Navbar />
      <main className="container py-4" style={{ maxWidth: '600px' }}>
        <h2 className={`mb-4 ${isDark ? 'text-light' : ''}`}>Checkout</h2>
        {error && <div className="alert alert-danger">{error}</div>}
        <form
          onSubmit={handleSubmit}
          className={`card p-4 shadow-sm border-0 ${isDark ? 'bg-dark-card border-secondary' : ''}`}
          style={isDark ? { backgroundColor: '#1e1e1e', borderColor: '#444' } : {}}
        >
          {['customerName', 'phone', 'city', 'buildingNo', 'street', 'area', 'pinCode'].map(field => (
            <div className="mb-3" key={field}>
              <label className={`form-label text-capitalize ${isDark ? 'text-light' : ''}`}>{field}</label>
              <input
                type="text"
                name={field}
                className={`form-control ${isDark ? 'bg-dark text-light border-secondary' : ''}`}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>
          ))}
          <button type="submit" className="btn btn-danger w-100" disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Confirm Order'}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutPage;